import React, { useEffect, useState, useRef } from "react";
import {
  addPhoto,
  getPhotos,
  updatePhoto,
  deletePhoto,
} from "../services/auth";

function HomePhoto() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({ srNo: "", image: null });
  const [editPhotoId, setEditPhotoId] = useState(null);
  const [editData, setEditData] = useState({ srNo: "", image: null });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const fileInputRef = useRef();
  const editFileInputRef = useRef();

  const fetchPhotos = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getPhotos();
      if (res.success) setPhotos(res.data);
      else setError(res.message || "Failed to fetch photos");
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") setFormData((prev) => ({ ...prev, image: files[0] }));
    else setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") setEditData((prev) => ({ ...prev, image: files[0] }));
    else setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!formData.srNo || !formData.image) {
      alert("Please provide srNo and select an image.");
      return;
    }

    const uploadData = new FormData();
    uploadData.append("srNo", formData.srNo);
    uploadData.append("image", formData.image);

    try {
      const data = await addPhoto(uploadData);
      if (data.success) {
        alert("Photo added successfully!");
        setFormData({ srNo: "", image: null });
        if (fileInputRef.current) fileInputRef.current.value = null;
        fetchPhotos();
      } else {
        alert(data.message || "Failed to add photo");
      }
    } catch (e) {
      console.error("Upload error:", e);
      alert("Something went wrong: " + e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this photo?")) return;

    try {
      const data = await deletePhoto(id);
      if (data.success) {
        alert("Photo deleted successfully!");
        fetchPhotos();
      } else {
        alert(data.message || "Failed to delete photo");
      }
    } catch (e) {
      alert(e.message);
    }
  };

  const startEdit = (photo) => {
    setEditPhotoId(photo.ID);
    setEditData({ srNo: photo.srNo, image: null });
    setIsMenuOpen(false);
  };

  const cancelEdit = () => {
    setEditPhotoId(null);
    setEditData({ srNo: "", image: null });
    if (editFileInputRef.current) editFileInputRef.current.value = null;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editData.srNo) {
      alert("Please provide srNo.");
      return;
    }
    const updateData = new FormData();
    updateData.append("srNo", editData.srNo);
    if (editData.image) updateData.append("image", editData.image);

    try {
      const data = await updatePhoto(editPhotoId, updateData);
      if (data.success) {
        alert("Photo updated successfully!");
        cancelEdit();
        fetchPhotos();
      } else {
        alert(data.message || "Failed to update photo");
      }
    } catch (e) {
      alert(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                ફોટા મેનેજ કરો
              </h1>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="h-8 sm:h-10 w-8 sm:w-10 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm sm:text-base font-semibold">
                {photos.length}
              </div>
              <span className="text-sm sm:text-base text-gray-700">
                Total Assets
              </span>
            </div>
          </div>
        </header>

        {/* Upload Card */}
        <div className="bg-white rounded-xl shadow-sm sm:shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-3 sm:mb-4 space-y-2 sm:space-y-0">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
              નવા ફોટો અપલોડ કરો
            </h2>
            <div className="text-xs sm:text-sm text-gray-500">
              <span className="font-medium">Max size:</span> 5MB
            </div>
          </div>

          <form onSubmit={handleCreate} className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  સીરીયલ નંબર
                </label>
                <input
                  type="number"
                  name="srNo"
                  value={formData.srNo}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                  required
                  placeholder="Sequence number"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ફોટો પસંદ કરો
                </label>
                <div className="flex items-center">
                  <input
                    type="file"
                    name="image"
                    accept="image/*"
                    onChange={handleInputChange}
                    className="hidden"
                    ref={fileInputRef}
                    required
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 rounded-l-lg cursor-pointer hover:bg-gray-50 text-xs sm:text-sm truncate"
                  >
                    {formData.image ? formData.image.name : "Choose file..."}
                  </label>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="px-3 sm:px-4 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg hover:bg-gray-200 text-xs sm:text-sm"
                  >
                    Browse
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 sm:px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors text-sm sm:text-base"
              >
                Upload
              </button>
            </div>
          </form>
        </div>

        {/* Status/Error */}
        {error && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-0.5">
                <svg
                  className="h-4 w-4 sm:h-5 sm:w-5 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-2 sm:ml-3">
                <p className="text-xs sm:text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Assets Table */}
        <div className="bg-white rounded-xl shadow-sm sm:shadow-md overflow-hidden">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                જૂના ફોટા
              </h2>
              <button
                onClick={fetchPhotos}
                disabled={loading}
                className="px-3 sm:px-4 py-1 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-3 w-3 sm:h-4 sm:w-4 text-gray-600"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Refreshing...
                  </span>
                ) : (
                  "Refresh"
                )}
              </button>
            </div>
          </div>

          {loading && photos.length === 0 ? (
            <div className="p-6 sm:p-8 flex justify-center items-center">
              <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Mobile View - Cards */}
              <div className="sm:hidden space-y-3 p-3">
                {photos.length === 0 && (
                  <div className="px-4 py-6 text-center text-gray-500">
                    No assets found. Upload your first asset above.
                  </div>
                )}

                {photos.map((photo) =>
                  editPhotoId === photo.ID ? (
                    <div
                      key={photo.ID}
                      className="bg-blue-50 rounded-lg p-4 space-y-3"
                    >
                      <div className="flex items-center justify-between">
                        <input
                          type="number"
                          name="srNo"
                          value={editData.srNo}
                          onChange={handleEditInputChange}
                          className="w-20 px-3 py-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      <div className="flex items-center space-x-3">
                        <img
                          src={`${photo.fullPath}`}
                          alt={`Photo ${photo.srNo}`}
                          className="h-12 w-12 object-cover rounded"
                        />
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Replace Image
                          </label>
                          <input
                            type="file"
                            name="image"
                            accept="image/*"
                            onChange={handleEditInputChange}
                            ref={editFileInputRef}
                            className="text-xs w-full"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={handleUpdate}
                          className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded hover:bg-green-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="px-3 py-1 bg-gray-500 text-white text-xs font-medium rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={photo.ID}
                      className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-900">
                          #{photo.srNo}
                        </span>
                        <div className="relative">
                          <button
                            onClick={() =>
                              setIsMenuOpen(
                                isMenuOpen === photo.ID ? null : photo.ID
                              )
                            }
                            className="p-1 rounded-full hover:bg-gray-200"
                          >
                            <svg
                              className="h-4 w-4 text-gray-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
                              />
                            </svg>
                          </button>
                          {isMenuOpen === photo.ID && (
                            <div className="absolute right-0 mt-1 w-32 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                              <div className="py-1">
                                <button
                                  onClick={() => startEdit(photo)}
                                  className="block w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-100"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(photo.ID)}
                                  className="block w-full text-left px-4 py-2 text-xs text-red-600 hover:bg-gray-100"
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <img
                          src={`${photo.fullPath}`}
                          alt={`Photo ${photo.srNo}`}
                          className="h-12 w-12 object-cover rounded"
                        />
                        <span className="text-xs text-gray-500">
                          Image {photo.srNo}
                        </span>
                      </div>
                    </div>
                  )
                )}
              </div>

              {/* Desktop View - Table */}
              <table className="hidden sm:table min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Sequence
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Preview
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {photos.length === 0 && (
                    <tr>
                      <td
                        colSpan="3"
                        className="px-6 py-4 text-center text-gray-500"
                      >
                        No assets found. Upload your first asset above.
                      </td>
                    </tr>
                  )}

                  {photos.map((photo) =>
                    editPhotoId === photo.ID ? (
                      <tr key={photo.ID} className="bg-blue-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            name="srNo"
                            value={editData.srNo}
                            onChange={handleEditInputChange}
                            className="w-20 px-3 py-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 text-sm"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-4">
                            <img
                              src={`${photo.fullPath}`}
                              alt={`Photo ${photo.srNo}`}
                              className="h-12 w-12 object-cover rounded"
                            />
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Replace Image
                              </label>
                              <input
                                type="file"
                                name="image"
                                accept="image/*"
                                onChange={handleEditInputChange}
                                ref={editFileInputRef}
                                className="text-sm"
                              />
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                          <button
                            onClick={handleUpdate}
                            className="px-3 py-1 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="px-3 py-1 bg-gray-500 text-white text-sm font-medium rounded hover:bg-gray-600"
                          >
                            Cancel
                          </button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={photo.ID} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {photo.srNo}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-12 w-12">
                              <img
                                src={`${photo.fullPath}`}
                                alt={`Photo ${photo.srNo}`}
                                className="h-12 w-12 object-cover rounded"
                              />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm text-gray-500">
                                Image {photo.srNo}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                          <button
                            onClick={() => startEdit(photo)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(photo.ID)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HomePhoto;
